import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, ISitemapModel, SITEMAP } from 'pbc.angular';

import { IKundenGruppe, IPostKundenGruppeResponse } from 'fa-kt.types';
import { KundenGruppesService } from '../../../query';
import { PostKundenGruppeCommandService } from '../service';

interface IFormValue {
  kundenGruppe?: IKundenGruppe;
}

@Component({
  selector: 'fa-kt-post-kunden-gruppe',
  templateUrl: './post-kunden-gruppe.component.html',
  styleUrls: ['./post-kunden-gruppe.component.css'],
})
export class PostKundenGruppeCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;
  model!: ISitemapModel;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IFormValue) {
    this.patch(value);
  }
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostKundenGruppeResponse> = new EventEmitter<IPostKundenGruppeResponse>();

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private actions: ActionService,
    private message: NzMessageService,
    public kundenGruppes: KundenGruppesService,
    public postKundenGruppe: PostKundenGruppeCommandService /* << dependencies    */ /*    dependencies >> */,
  ) {
    this.command = sitemap.KUNDEN.Commands.POST_KUNDEN_GRUPPE;
    this.model = sitemap.KUNDEN.Models.KUNDEN_GRUPPE;
    /* << constructor    */
    /*    constructor >> */
    this.form = this.fb.group({
      kundenGruppe: this.fb.group({
        id: [null, []],
        name: [null, [Validators.required]],
        beschreibung: [null, []],
      }),
    });
    this.patch();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value: any) => {
        this.valid.emit(this.form.valid);
        if (this.form.valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.id?.valueChanges.subscribe(async (id) => {
        /* << on-id-change    */
        /*    on-id-change >> */
      }) as Subscription,
      this.name?.valueChanges.subscribe(async (name) => {
        /* << on-name-change    */
        /*    on-name-change >> */
      }) as Subscription,
      this.beschreibung?.valueChanges.subscribe(async (beschreibung) => {
        /* << on-beschreibung-change    */
        /*    on-beschreibung-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IFormValue) {
    let kundenGruppe = {
      id: null,
      name: null,
      beschreibung: null,
    };
    if (value && value.kundenGruppe) {
      kundenGruppe = {
        ...kundenGruppe,
        ...cleanObject(value.kundenGruppe),
      };
    }
    this.form.patchValue({ kundenGruppe });
    /* << custom-stuff    */
    /*    custom-stuff >> */
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload && payload.kundenGruppe) {
        payload.kundenGruppe = cleanObject(payload.kundenGruppe);
      }
      const response: IPostKundenGruppeResponse | undefined = await this.postKundenGruppe.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls.objektArt.get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get id() {
    return this.form.controls.kundenGruppe.get('id');
  }

  get name() {
    return this.form.controls.kundenGruppe.get('name');
  }

  get beschreibung() {
    return this.form.controls.kundenGruppe.get('beschreibung');
  }
}
